/**
 * EventPady+ Axios Client
 *
 * Responsibilities:
 *   1. Attach JWT access token from in-memory store to every request.
 *   2. Attach X-Request-ID (UUIDv4) for server-side log correlation.
 *   3. On 401, attempt a silent token refresh by sending the stored refresh token.
 *   4. Queue concurrent requests during a refresh so only one refresh fires.
 *   5. On refresh failure, clear the token and redirect to the login page.
 *   6. Normalize all non-2xx error responses to the canonical ApiError shape.
 *
 * Per SECURITY.md §3 — access tokens are NEVER written to localStorage or
 * sessionStorage. Refresh tokens are persisted in sessionStorage so a page
 * reload silently restores the session (sessionStorage is cleared when the
 * tab closes, giving session-scoped lifetime without indefinite persistence).
 */
import axios, {
  type AxiosInstance,
  type InternalAxiosRequestConfig,
  type AxiosError,
  type AxiosResponse,
} from 'axios'
import { API_ENDPOINTS } from './endpoints'
import { ROUTES } from '@/constants/routes'
import type { ApiError } from '@/types'

// ---------------------------------------------------------------------------
// Token store
// Access tokens: in-memory only (never touch any storage).
// Refresh tokens: in-memory + sessionStorage fallback so page reloads restore
//                 the session without requiring a new login.
//                 sessionStorage is cleared on tab close → session-scoped lifetime.
// Exported so AuthContext can read/write the tokens.
// ---------------------------------------------------------------------------
const _SESSION_RT_KEY = '_ep_rt'

let _accessToken: string | null = null
let _refreshToken: string | null = null

export const tokenStore = {
  // Access token — memory only
  get: (): string | null => _accessToken,
  set: (token: string | null): void => {
    _accessToken = token
  },

  // Refresh token — memory + sessionStorage
  getRefresh: (): string | null => {
    if (_refreshToken) return _refreshToken
    // On page reload the in-memory value is gone; try sessionStorage.
    try {
      const stored = sessionStorage.getItem(_SESSION_RT_KEY)
      if (stored) {
        _refreshToken = stored // re-populate memory cache
      }
      return _refreshToken
    } catch {
      // sessionStorage unavailable (e.g. private-browsing restriction)
      return null
    }
  },
  setRefresh: (token: string | null): void => {
    _refreshToken = token
    try {
      if (token) {
        sessionStorage.setItem(_SESSION_RT_KEY, token)
      } else {
        sessionStorage.removeItem(_SESSION_RT_KEY)
      }
    } catch {
      // sessionStorage unavailable — memory-only fallback is acceptable
    }
  },
}

// ---------------------------------------------------------------------------
// Refresh queue — prevents duplicate refresh calls when multiple 401s fire
// ---------------------------------------------------------------------------
let _isRefreshing = false
let _refreshQueue: Array<(token: string | null) => void> = []

function processRefreshQueue(token: string | null): void {
  _refreshQueue.forEach((resolve) => {
    resolve(token)
  })
  _refreshQueue = []
}

// ---------------------------------------------------------------------------
// Error normalisation
// Ensures every rejected promise carries a response body that matches ApiError.
// ---------------------------------------------------------------------------
function unwrapResponse<T>(payload: T | { data: T } | null | undefined): T | null {
  if (!payload) return null
  if (typeof payload === 'object' && payload !== null && 'data' in payload) {
    return (payload as { data: T }).data
  }
  return payload as T
}

function normalizeErrorResponse(error: AxiosError): void {
  if (!error.response) return

  const raw = error.response.data as Record<string, unknown> | null | undefined

  if (raw && typeof raw === 'object' && 'error' in raw) {
    return
  }

  const detail =
    typeof (raw as Record<string, unknown> | undefined)?.detail === 'string'
      ? ((raw as Record<string, unknown>).detail as string)
      : undefined

  const normalised: ApiError = {
    error: {
      code: error.response.status === 404 ? 'NOT_FOUND' : 'SERVER_ERROR',
      message: detail ?? error.message ?? 'An unexpected error occurred.',
    },
  }

  error.response.data = normalised
}

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------
const rawApiBaseUrl = import.meta.env.VITE_API_URL ?? '/api/v1'
const normalizedApiBaseUrl = rawApiBaseUrl === '/api' || rawApiBaseUrl === '/api/'
  ? '/api/v1'
  : rawApiBaseUrl.endsWith('/api/v1')
    ? rawApiBaseUrl
    : `${rawApiBaseUrl.replace(/\/$/, '')}/api/v1`

export const apiClient: AxiosInstance = axios.create({
  baseURL: normalizedApiBaseUrl,
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
  // 15-second hard deadline — prevents auth from hanging forever when the
  // backend is starting up, overloaded, or unreachable, which caused the
  // "keeps loading on white screen" symptom (isLoading stayed true forever).
  timeout: 15_000,
})

// ── Request interceptor ─────────────────────────────────────────────────────
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
    const token = tokenStore.get()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    config.headers['X-Request-ID'] = crypto.randomUUID()
    return config
  },
)

// ── Response interceptor — silent token refresh on 401 ─────────────────────
interface RetryableRequest extends InternalAxiosRequestConfig {
  _retry?: boolean
}

apiClient.interceptors.response.use(
  (response: AxiosResponse) => response,

  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableRequest | undefined

    const is401 = error.response?.status === 401
    const isRefreshCall = originalRequest?.url?.includes(API_ENDPOINTS.AUTH.REFRESH)
    const alreadyRetried = originalRequest?._retry === true

    normalizeErrorResponse(error)

    if (!is401 || isRefreshCall || alreadyRetried || !originalRequest) {
      return Promise.reject(error)
    }

    // Check if we have a refresh token to use
    const storedRefreshToken = tokenStore.getRefresh()
    if (!storedRefreshToken) {
      // No refresh token in memory — send user to login
      tokenStore.set(null)
      if (window.location.pathname !== ROUTES.AUTH.LOGIN) {
        window.location.href = ROUTES.AUTH.LOGIN
      }
      return Promise.reject(error)
    }

    // If another refresh is already in progress, queue this request
    if (_isRefreshing) {
      return new Promise<string | null>((resolve) => {
        _refreshQueue.push(resolve)
      }).then((newToken) => {
        if (!newToken) return Promise.reject(error)
        originalRequest.headers.Authorization = `Bearer ${newToken}`
        return apiClient(originalRequest)
      })
    }

    originalRequest._retry = true
    _isRefreshing = true

    try {
      const { data } = await apiClient.post<
        { access_token?: string; refresh_token?: string } | { data: { access_token?: string; refresh_token?: string } }
      >(API_ENDPOINTS.AUTH.REFRESH, { refresh_token: storedRefreshToken })

      const refreshPayload = unwrapResponse<{ access_token?: string; refresh_token?: string }>(data)
      const newAccessToken = refreshPayload?.access_token ?? null
      const newRefreshToken = refreshPayload?.refresh_token ?? null

      if (!newAccessToken) {
        throw new Error('Token refresh did not return an access token')
      }

      tokenStore.set(newAccessToken)
      if (newRefreshToken) tokenStore.setRefresh(newRefreshToken)

      processRefreshQueue(newAccessToken)
      originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
      return await apiClient(originalRequest)
    } catch (refreshError) {
      tokenStore.set(null)
      tokenStore.setRefresh(null)
      processRefreshQueue(null)
      if (window.location.pathname !== ROUTES.AUTH.LOGIN) {
        window.location.href = ROUTES.AUTH.LOGIN
      }
      throw refreshError instanceof Error
        ? refreshError
        : new Error('Token refresh failed')
    } finally {
      _isRefreshing = false
    }
  },
)
